import { Apollo, APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { ApplicationConfig, Injector, inject } from '@angular/core';
import { ApolloClientOptions, ApolloLink, InMemoryCache } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { Router } from '@angular/router';
import { environment } from '@envs/environment';
import { AuthStore } from './store/auth.store';
import { HttpHeaders } from '@angular/common/http';
import { AdminPermissionService } from '@services/admin-permission.service';
import { isMissingPermissionError } from '@shared/utils/admin-permission-error.util';

const uri = environment.apiUrl_okito_official + 'graphql'; // <-- using existing apiUrl_okito_official with /graphql

export function apolloOptionsFactory(): ApolloClientOptions {
  const httpLink = inject(HttpLink);
  const authStore = inject(AuthStore);
  const router = inject(Router);
  // Resolved lazily: the permission service itself depends on Apollo.
  const injector = inject(Injector);
 
  const basic = setContext((operation, context) => {
    const headers =
      context['headers'] instanceof HttpHeaders
        ? context['headers']
        : new HttpHeaders();

    return {
      headers: headers.set(
        'Accept',
        'application/json, application/graphql-response+json; charset=utf-8'
      ),
    };
  });
 
  const auth = setContext((operation, context) => {
    const token = authStore.currentState.token;
 
    if (!token) {
      return {};
    } else {
      const headers =
        context['headers'] instanceof HttpHeaders
          ? context['headers']
          : new HttpHeaders();

      return {
        headers: headers.set('Authorization', `${token}`),
      };
    }
  });

  // GraphQL auth errors arrive as HTTP 200 with an errors array, so the
  // Angular HTTP interceptor never sees them — handle them here instead.
  //
  // Some errors come back with the same "Unauthorized" message / 401-403
  // status as a real session failure but for an unrelated reason (e.g. a
  // document-upload endpoint's own access-key gate) — those must not log
  // the admin out. Keep this set in sync with NON_SESSION_ERROR_REASONS in
  // auth.interceptor.ts.
  //
  // `missing_permission` is the admin's roles refusing the call: the session is
  // fine, so instead of logging out, refresh the cached permissions — the role
  // may have changed since they were loaded, and the nav should catch up.
  const NON_SESSION_ERROR_REASONS = new Set([
    'unverified_accesskey',
    'missing_accesskey',
    'missing_permission',
  ]);

  const errorLink = new ErrorLink(({ error }) => {
    if (isMissingPermissionError(error)) {
      injector.get(AdminPermissionService).refresh();
    }

    const isGraphQLAuthError =
      CombinedGraphQLErrors.is(error) &&
      error.errors.some(
        (e) =>
          !NON_SESSION_ERROR_REASONS.has(e.extensions?.['reason'] as string) &&
          (e.message === 'Unauthorized' ||
            e.extensions?.['code'] === 'UNAUTHENTICATED' ||
            e.extensions?.['code'] === 'FORBIDDEN')
      );

    const networkStatus = (error as any)?.statusCode ?? (error as any)?.status;
    const networkReason = (error as any)?.reason;
    const isNetworkAuthError =
      (networkStatus === 401 || networkStatus === 403) &&
      !NON_SESSION_ERROR_REASONS.has(networkReason);

    if ((isGraphQLAuthError || isNetworkAuthError) && !router.url.startsWith('/admin/login')) {
      authStore.logout().subscribe(() => {
        router.navigate(['/admin/login']);
      });
    }
  });

  return {
    link: ApolloLink.from([basic, auth, errorLink, httpLink.create({ uri })]),
    cache: new InMemoryCache(),
  };
}

export const graphqlProvider: ApplicationConfig['providers'] = [
  Apollo,
  {
    provide: APOLLO_OPTIONS,
    useFactory: apolloOptionsFactory,
  },
];
